import { ApiError, type ApiErrorResponse } from "@models/error";

/**
 * The search service client shared by every capability API: base URL, the
 * bearer token (set once by `ContextProvider`), and the service's error body
 * (`{ error_code, message }`) turned into an `ApiError`.
 */
const BASE_URL = import.meta.env.VITE_SEARCH_API_BASE_URL ?? "";

let getAccessToken: (() => Promise<string | null>) | null = null;

export const setSearchAccessTokenFn = (
  fn: () => Promise<string | null>,
): void => {
  getAccessToken = fn;
};

async function headers(): Promise<Record<string, string>> {
  const result: Record<string, string> = { "Content-Type": "application/json" };
  const token = getAccessToken ? await getAccessToken() : null;
  if (token) result.Authorization = `Bearer ${token}`;
  return result;
}

/** FastAPI's own errors carry ``detail`` instead of the service's body:
 *  a string (403 permission / API-key limits), an object (429 licence usage
 *  limit: ``{ error, message }``) or a list of ``{ msg }`` (422 validation). */
type FastApiDetail = string | { error?: string; message?: string } | { msg?: string }[];

function detailMessage(detail: FastApiDetail | undefined): string | undefined {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.find((item) => item.msg)?.msg;
  return detail?.message;
}

async function readError(response: Response): Promise<ApiError> {
  let body: Partial<ApiErrorResponse> & { detail?: FastApiDetail } = {};
  try {
    body = (await response.json()) as typeof body;
  } catch {
    // Not JSON (gateway error page): the status alone has to do.
  }
  const message = body.message ?? detailMessage(body.detail);
  const fastApiCode =
    body.detail && typeof body.detail === "object" && !Array.isArray(body.detail)
      ? body.detail.error
      : undefined;
  return new ApiError(
    `Search failed: ${response.status}${message ? `: ${message}` : ""}`,
    body.error_code ?? fastApiCode ?? "http_error",
    response.status,
    body.details,
  );
}

export async function postJson<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(`${BASE_URL}${path}`, {
    method: "POST",
    headers: await headers(),
    body: JSON.stringify(body),
  });
  if (!response.ok) throw await readError(response);
  return (await response.json()) as T;
}

export async function getJson<T>(
  path: string,
  params: Record<string, string | undefined> = {},
): Promise<T> {
  const query = new URLSearchParams(
    Object.entries(params).filter((entry): entry is [string, string] => !!entry[1]),
  ).toString();
  const response = await fetch(`${BASE_URL}${path}${query ? `?${query}` : ""}`, {
    method: "GET",
    headers: await headers(),
  });
  if (!response.ok) throw await readError(response);
  return (await response.json()) as T;
}
